import { fetchWithAuth } from "../utils/fetchWithAuth";
import competitorAnalysisManifest from "../shared/featureWorkflows/competitorAnalysisManifest";

class CompetitorAnalysisWorkflowService {
  constructor() {
    this.WORKFLOW_STEPS = competitorAnalysisManifest.steps.map(step => ({
      ...step,
      status: "pending",
    }));

    this.workflowResults = {};
  }

  getSteps() {
    return [...this.WORKFLOW_STEPS];
  }

  resetWorkflow() {
    this.workflowResults = {};
    this.WORKFLOW_STEPS.forEach(step => {
      step.status = "pending";
    });
  }

  async executeWorkflow(params, callbacks) {
    const {
      designChallenge,
      deepAnalysis,
      focusAreas,
      depthLevel,
      customContext,
      planId,
    } = params;

    const { onStepStart, onStepComplete, onComplete, onError, onInsufficientCredits } = callbacks;

    // Check credits before starting
    try {
      const creditCheck = await fetchWithAuth('/api/credits/check?action=competitor-analysis');
      if (creditCheck.ok) {
        const creditData = await creditCheck.json();
        if (!creditData.allowed) {
          if (onInsufficientCredits) onInsufficientCredits();
          else if (onError) onError(Object.assign(new Error('Insufficient credits'), { type: 'insufficient_credits' }));
          return null;
        }
      }
    } catch (_) {}

    console.log("🔬 COMPETITOR ANALYSIS WORKFLOW: Starting", {
      designChallenge: designChallenge?.substring(0, 100) + "...",
      focusAreas,
      depthLevel,
      hasDeepAnalysis: !!deepAnalysis,
    });

    try {
      const steps = this.getSteps();

      // Step 1: Research Methodology
      if (onStepStart) onStepStart(0, { ...steps[0], agent: "UX Research Lead" });

      const methodologyResults = await this.executeResearchMethodology(
        designChallenge,
        deepAnalysis,
        focusAreas,
        planId,
      );
      console.log("📝 Research Methodology completed");
      if (onStepComplete)
        onStepComplete(0, { ...steps[0], status: "completed" }, methodologyResults);

      // Step 2: Market Intelligence
      if (onStepStart) onStepStart(1, { ...steps[1], agent: "UX Research Lead" });

      const marketResults = await this.executeMarketIntelligence(
        designChallenge,
        deepAnalysis,
        methodologyResults,
        focusAreas,
        depthLevel,
        planId,
      );
      console.log("📝 Market Intelligence completed");
      if (onStepComplete)
        onStepComplete(1, { ...steps[1], status: "completed" }, marketResults);

      // Step 3: Opportunity Analysis
      if (onStepStart) onStepStart(2, { ...steps[2], agent: "UX Research Lead" });

      const opportunityResults = await this.executeOpportunityAnalysis(
        designChallenge,
        deepAnalysis,
        methodologyResults,
        marketResults,
        focusAreas,
        depthLevel,
        planId,
      );
      console.log("📝 Opportunity Analysis completed");
      if (onStepComplete)
        onStepComplete(2, { ...steps[2], status: "completed" }, opportunityResults);

      // Step 4: Report Synthesis
      if (onStepStart) onStepStart(3, { ...steps[3], agent: "UX Research Lead" });

      const reportResults = await this.executeReportSynthesis(
        designChallenge,
        deepAnalysis,
        methodologyResults,
        marketResults,
        opportunityResults,
        focusAreas,
        depthLevel,
        customContext,
        planId,
      );
      console.log("📝 Report Synthesis completed");
      if (onStepComplete)
        onStepComplete(3, { ...steps[3], status: "completed" }, reportResults);

      // Compile final results
      const finalReport = {
        methodology: methodologyResults,
        marketIntelligence: marketResults,
        opportunityAnalysis: opportunityResults,
        report: reportResults,
        metadata: {
          focusAreas,
          depthLevel,
          generatedAt: new Date().toISOString(),
        },
      };

      if (onComplete) onComplete(finalReport);

      // Deduct credits after successful completion
      try {
        const deductRes = await fetchWithAuth('/api/credits/deduct', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'competitor-analysis' }),
        });
        if (deductRes.ok) {
          const deductData = await deductRes.json();
          if (!deductData.success && onInsufficientCredits) onInsufficientCredits();
        }
      } catch (_) {}

      return finalReport;

    } catch (error) {
      console.error("❌ Competitor Analysis workflow failed:", error);
      if (onError) onError(error);
      throw error;
    }
  }

  async executeResearchMethodology(designChallenge, deepAnalysis, focusAreas, planId = null) {
    try {
      const result = await fetchWithAuth(
        "/api/autonomous-ai/autonomous-research-planning",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            design_challenge: designChallenge,
            deep_analysis: deepAnalysis,
            research_type: "contextual_adaptive",
            plan_id: planId,
            agentId: "2",
            context: {
              source: "competitor_analysis_workflow",
              step: "research_methodology",
              focusAreas,
            },
          }),
        },
      );

      if (!result.ok) {
        throw new Error(`Research methodology failed with status ${result.status}`);
      }

      const data = await result.json();
      this.workflowResults.methodology = data;
      return data;
    } catch (error) {
      console.error("Research methodology step failed:", error);
      throw error;
    }
  }

  async executeMarketIntelligence(
    designChallenge,
    deepAnalysis,
    methodology,
    focusAreas,
    depthLevel,
    planId = null,
  ) {
    try {
      const result = await fetchWithAuth(
        "/api/autonomous-ai/competitor-market-intelligence",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            design_challenge: designChallenge,
            deep_analysis: deepAnalysis,
            methodology,
            focus_areas: focusAreas,
            depth_level: depthLevel,
            plan_id: planId,
            agentId: "2",
            context: {
              source: "competitor_analysis_workflow",
              step: "market_intelligence",
            },
          }),
        },
      );

      if (!result.ok) {
        throw new Error(`Market intelligence failed with status ${result.status}`);
      }

      const data = await result.json();
      this.workflowResults.marketIntelligence = data;
      return data;
    } catch (error) {
      console.error("Market intelligence step failed:", error);
      throw error;
    }
  }

  async executeOpportunityAnalysis(
    designChallenge,
    deepAnalysis,
    methodology,
    marketIntelligence,
    focusAreas,
    depthLevel,
    planId = null,
  ) {
    try {
      const result = await fetchWithAuth(
        "/api/autonomous-ai/competitor-opportunity-analysis",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            design_challenge: designChallenge,
            deep_analysis: deepAnalysis,
            methodology,
            market_intelligence: marketIntelligence,
            focus_areas: focusAreas,
            depth_level: depthLevel,
            plan_id: planId,
            agentId: "2",
            context: {
              source: "competitor_analysis_workflow",
              step: "opportunity_analysis",
            },
          }),
        },
      );

      if (!result.ok) {
        throw new Error(`Opportunity analysis failed with status ${result.status}`);
      }

      const data = await result.json();
      this.workflowResults.opportunityAnalysis = data;
      return data;
    } catch (error) {
      console.error("Opportunity analysis step failed:", error);
      throw error;
    }
  }

  async executeReportSynthesis(
    designChallenge,
    deepAnalysis,
    methodology,
    marketIntelligence,
    opportunityAnalysis,
    focusAreas,
    depthLevel,
    customContext,
    planId = null,
  ) {
    try {
      const result = await fetchWithAuth(
        "/api/autonomous-ai/competitor-report-synthesis",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            design_challenge: designChallenge,
            deep_analysis: deepAnalysis,
            methodology,
            market_intelligence: marketIntelligence,
            opportunity_analysis: opportunityAnalysis,
            focus_areas: focusAreas,
            depth_level: depthLevel,
            custom_context: customContext,
            plan_id: planId,
            agentId: "2",
            context: {
              source: "competitor_analysis_workflow",
              step: "report_synthesis",
            },
          }),
        },
      );

      if (!result.ok) {
        throw new Error(`Report synthesis failed with status ${result.status}`);
      }

      const data = await result.json();
      this.workflowResults.report = data;
      return data;
    } catch (error) {
      console.error("Report synthesis step failed:", error);
      throw error;
    }
  }

  async saveReport(planId, planName, reportData) {
    try {
      const result = await fetchWithAuth(
        "/api/competitor-analysis/save",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planId,
            planName,
            designChallenge: reportData.designChallenge,
            focusAreas: reportData.focusAreas,
            depthLevel: reportData.depthLevel,
            customContext: reportData.customContext,
            methodology: reportData.methodology,
            marketIntelligence: reportData.marketIntelligence,
            opportunityAnalysis: reportData.opportunityAnalysis,
            report: reportData.report,
          }),
        },
      );

      if (!result.ok) {
        throw new Error(`Save report failed with status ${result.status}`);
      }

      const data = await result.json();
      console.log("✅ Report saved:", data);
      return data;
    } catch (error) {
      console.error("Failed to save report:", error);
      throw error;
    }
  }

  async loadLatestReport(planId) {
    try {
      const result = await fetchWithAuth(
        `/api/competitor-analysis/plan/${planId}/latest`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!result.ok) {
        throw new Error(`Load report failed with status ${result.status}`);
      }

      const data = await result.json();
      if (data.report) {
        this.workflowResults = {
          methodology: data.report.methodology,
          marketIntelligence: data.report.market_intelligence,
          opportunityAnalysis: data.report.opportunity_analysis,
          report: data.report.report,
        };
      }
      return data;
    } catch (error) {
      console.error("Failed to load report:", error);
      throw error;
    }
  }

  async loadReportHistory(planId) {
    try {
      const result = await fetchWithAuth(
        `/api/competitor-analysis/plan/${planId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!result.ok) {
        throw new Error(`Load history failed with status ${result.status}`);
      }

      const data = await result.json();
      return data.reports || [];
    } catch (error) {
      console.error("Failed to load report history:", error);
      throw error;
    }
  }

  async loadReport(reportId) {
    try {
      const result = await fetchWithAuth(
        `/api/competitor-analysis/${reportId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!result.ok) {
        throw new Error(`Load report failed with status ${result.status}`);
      }

      const data = await result.json();
      this.workflowResults = {
        methodology: data.methodology,
        marketIntelligence: data.market_intelligence,
        opportunityAnalysis: data.opportunity_analysis,
        report: data.report,
      };
      return data;
    } catch (error) {
      console.error("Failed to load report:", error);
      throw error;
    }
  }

  async deleteReport(reportId) {
    try {
      const result = await fetchWithAuth(
        `/api/competitor-analysis/${reportId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!result.ok) {
        throw new Error(`Delete report failed with status ${result.status}`);
      }

      const data = await result.json();
      console.log("✅ Report deleted:", data);
      return data;
    } catch (error) {
      console.error("Failed to delete report:", error);
      throw error;
    }
  }
}

const competitorAnalysisWorkflowService = new CompetitorAnalysisWorkflowService();
export default competitorAnalysisWorkflowService;
